import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Group, SimpleGrid, Stack } from '@mantine/core'
import { FiChevronUp, FiChevronDown } from 'react-icons/fi'
import agent from '../../app/api/agent'
import LoadingComponent from '../../app/layout/LoadingComponents'
import { AppBadge, AppButton, AppCard, AppIconButton, AppModal, AppText, AuthGateCta, ReportContentModal } from '../../app/common/ui'
import { FlagTargetType } from '../../app/models/flag'
import { useStore } from '../../app/stores/store'
import type { Part, PartFormValues } from '../../app/models/part'
import PartFormModal from '../inventory/PartFormModal'
import CommunityPhotosGallery from '../contentPhotos/CommunityPhotosGallery'

function formatCurrency (value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

export default observer(function PartDetail () {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const { userStore, voteStore, partStore } = useStore()
  const { updatePart, deletePart, loading: partStoreLoading } = partStore

  const [part, setPart] = useState<Part | null>(null)
  const [loading, setLoading] = useState(true)
  const [editingPart, setEditingPart] = useState<Part | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)

  const loadPart = useCallback(() => {
    if (!id || !userStore.isLoggedIn) {
      setPart(null)
      setLoading(false)
      return
    }
    setLoading(true)
    agent.Parts.details(id)
      .then(setPart)
      .catch(() => setPart(null))
      .finally(() => setLoading(false))
  }, [id, userStore.isLoggedIn])

  useEffect(() => {
    loadPart()
  }, [loadPart])

  const handlePartVote = async (value: number) => {
    if (!part) return
    const isOwn = userStore.user?.username === part.authorUsername
    if (isOwn) return
    try {
      if (part.currentUserVote === value) {
        await agent.Votes.remove(agent.VoteTargetType.Part, part.id)
      } else {
        await agent.Votes.cast(agent.VoteTargetType.Part, part.id, value)
      }
      loadPart()
    } catch {
      /* interceptor */
    }
  }

  const handleUpdateStandalone = async (partId: string, values: PartFormValues) => {
    await updatePart(partId, values)
    setEditingPart(null)
    loadPart()
  }

  const handleConfirmDelete = async () => {
    if (!part) return
    try {
      await deletePart(part.id)
      toast.success('Part deleted.')
      history.push('/parts-bin')
    } catch {
      /* interceptor */
    } finally {
      setDeleteOpen(false)
    }
  }

  if (!userStore.isLoggedIn) {
    return (
      <Box py="xl">
        <AuthGateCta action="view part details" pitch="Sign in to open this part and community photos." />
      </Box>
    )
  }

  if (loading) {
    return <LoadingComponent content="Loading part…" />
  }

  if (!part) {
    return (
      <Box py="xl">
        <AppText>Part not found or you do not have access.</AppText>
        <AppButton mt="md" variant="subtle" onClick={() => history.push('/parts-bin')}>
          Back to Parts Bin
        </AppButton>
      </Box>
    )
  }

  const platformChips =
    part.compatiblePlatforms
      ?.split(/[,;]+/)
      .map((s) => s.trim())
      .filter(Boolean) ?? []

  const canReportPart =
    userStore.isLoggedIn &&
    userStore.user?.username !== part.authorUsername

  return (
    <Box>
      <AppCard
        padding="lg"
        radius="md"
        sx={{
          background: 'var(--gs-surface)',
          border: '1px solid var(--gs-border)',
          marginBottom: 'var(--gs-lg)'
        }}
      >
        <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]} spacing="lg">
          <Box>
            {part.imageUrl
              ? (
              <Box
                component="img"
                src={part.imageUrl}
                alt=""
                sx={{
                  width: '100%',
                  maxHeight: 320,
                  objectFit: 'cover',
                  borderRadius: 8,
                  border: '1px solid var(--gs-border)'
                }}
              />
                )
              : (
              <Box
                sx={{
                  width: '100%',
                  minHeight: 200,
                  borderRadius: 8,
                  border: '1px dashed var(--gs-border)',
                  background: 'var(--gs-surface2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--gs-md)'
                }}
              >
                <AppText align="center" color="dimmed" weight={600}>
                  {part.name}
                </AppText>
              </Box>
                )}
          </Box>
          <Stack spacing="sm">
            <AppText
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 28,
                lineHeight: 1.2
              }}
            >
              {part.name}
            </AppText>
            {(part.manufacturer || part.partNumber) && (
              <AppText size="sm" color="dimmed">
                {[part.manufacturer, part.partNumber].filter(Boolean).join(' · ')}
              </AppText>
            )}
            <AppText size="xl" weight={700} sx={{ color: 'var(--gs-gold)', fontVariantNumeric: 'tabular-nums' }}>
              {formatCurrency(part.value)}
            </AppText>
            <Group spacing="xs">
              {part.condition && (
                <AppBadge size="sm" variant="light" radius="xl">
                  {part.condition}
                </AppBadge>
              )}
              {part.category && (
                <AppBadge size="sm" variant="outline" radius="sm">
                  {part.category}
                </AppBadge>
              )}
            </Group>
            {platformChips.length > 0 && (
              <Group spacing="xs">
                {platformChips.map((p) => (
                  <AppBadge key={p} size="sm" variant="dot" radius="sm">
                    {p}
                  </AppBadge>
                ))}
              </Group>
            )}
            <Group spacing="xs" mt="xs">
              {userStore.user?.username !== part.authorUsername && (
                <>
                  <AppIconButton
                    size="sm"
                    variant={part.currentUserVote === 1 ? 'filled' : 'subtle'}
                    color="gray"
                    onClick={() => void handlePartVote(1)}
                    disabled={voteStore.loading}
                  >
                    <FiChevronUp size={14} />
                  </AppIconButton>
                  <AppText size="sm" weight={500} sx={{ minWidth: 16, textAlign: 'center' }}>
                    {part.voteScore ?? 0}
                  </AppText>
                  <AppIconButton
                    size="sm"
                    variant={part.currentUserVote === -1 ? 'filled' : 'subtle'}
                    color="gray"
                    onClick={() => void handlePartVote(-1)}
                    disabled={voteStore.loading}
                  >
                    <FiChevronDown size={14} />
                  </AppIconButton>
                </>
              )}
              {userStore.user?.username === part.authorUsername && (part.voteScore ?? 0) !== 0 && (
                <AppText size="sm" color="dimmed">
                  Score: {part.voteScore ?? 0}
                </AppText>
              )}
              {canReportPart && (
                <AppButton size="xs" variant="outline" color="red" onClick={() => setReportOpen(true)}>
                  Report
                </AppButton>
              )}
            </Group>
          </Stack>
        </SimpleGrid>
      </AppCard>

      <AppCard
        padding="lg"
        radius="md"
        sx={{
          background: 'var(--gs-surface)',
          border: '1px solid var(--gs-border)',
          marginBottom: 'var(--gs-lg)'
        }}
      >
        <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 16, marginBottom: 12 }}>
          Details
        </AppText>
        {part.description
          ? (
          <AppText size="sm" sx={{ whiteSpace: 'pre-wrap', marginBottom: 12 }}>
            {part.description}
          </AppText>
            )
          : (
          <AppText size="sm" color="dimmed" mb="sm">
            No description.
          </AppText>
            )}
        {part.notes
          ? (
          <AppText size="sm" color="dimmed" sx={{ whiteSpace: 'pre-wrap', marginBottom: 12 }}>
            <AppText component="span" weight={600} color="var(--gs-text)">
              Notes:{' '}
            </AppText>
            {part.notes}
          </AppText>
            )
          : null}
        <AppText size="sm" color="dimmed" mb="sm">
          Used in{' '}
          <AppText component="span" weight={600} color="var(--gs-text)">
            {part.usedInBuildsCount ?? 0}
          </AppText>{' '}
          build{(part.usedInBuildsCount ?? 0) === 1 ? '' : 's'}
        </AppText>
        {(part.firearmModelNames?.length ?? 0) > 0 && (
          <Box>
            <AppText size="xs" color="dimmed" mb={6}>
              Compatible firearm models
            </AppText>
            <Group spacing="xs">
              {part.firearmModelNames!.map((n) => (
                <AppBadge key={n} size="sm" variant="light" radius="sm">
                  {n}
                </AppBadge>
              ))}
            </Group>
          </Box>
        )}
      </AppCard>

      {id && <CommunityPhotosGallery entityType="Part" entityId={id} />}

      {userStore.isAdmin && (
        <Group mt="xl" spacing="sm">
          <AppButton variant="light" onClick={() => setEditingPart(part)}>
            Edit
          </AppButton>
          <AppButton color="red" variant="outline" onClick={() => setDeleteOpen(true)}>
            Delete
          </AppButton>
        </Group>
      )}

      <PartFormModal
        opened={!!editingPart}
        onClose={() => setEditingPart(null)}
        onSave={() => {}}
        mode="standalone"
        initialPartFromBin={editingPart}
        onUpdateStandalone={handleUpdateStandalone}
        loading={partStoreLoading}
      />

      <AppModal opened={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete part">
        <AppText size="sm" mb="md">
          Delete this part permanently?
        </AppText>
        <Group position="right">
          <AppButton variant="default" onClick={() => setDeleteOpen(false)}>
            Cancel
          </AppButton>
          <AppButton color="red" onClick={() => void handleConfirmDelete()}>
            Delete
          </AppButton>
        </Group>
      </AppModal>

      <ReportContentModal
        opened={reportOpen}
        onClose={() => setReportOpen(false)}
        targetType={FlagTargetType.Part}
        targetId={part.id}
        contentLabel="part"
      />
    </Box>
  )
})
